/**
 * Permit hours on a job: windows of weekdays with a start and end time (an end at or before the start
 * runs into the next morning). Used for the start of shift check "within the approved hours".
 */
import type { PermitWindow } from "../../lib/supabase";

const DAYS = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [0, "Sun"]] as const;
const DAY_NAME = Object.fromEntries(DAYS) as Record<number, string>;

const hm12 = (hm: string) => {
  const [h, m] = hm.split(":").map(Number) as [number, number];
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
};

/** "Mon to Fri 8pm to 5am" style summary. */
export function describePermitHours(windows: PermitWindow[] | null | undefined): string {
  if (!windows?.length) return "";
  return windows
    .map((w) => {
      const order = DAYS.map(([d]) => d).filter((d) => w.days.includes(d));
      const idx = order.map((d) => DAYS.findIndex(([x]) => x === d));
      const run = idx.length > 2 && idx.every((v, i) => i === 0 || v === idx[i - 1]! + 1);
      const days = run ? `${DAY_NAME[order[0]!]} to ${DAY_NAME[order[order.length - 1]!]}` : order.map((d) => DAY_NAME[d]).join(", ");
      return `${days} ${hm12(w.start)} to ${hm12(w.end)}`;
    })
    .join("; ");
}

export function PermitHoursEditor({ value, onChange }: { value: PermitWindow[] | null | undefined; onChange: (v: PermitWindow[] | null) => void }) {
  const rows = value ?? [];
  const put = (i: number, w: PermitWindow) => onChange(rows.map((r, j) => (j === i ? w : r)));
  return (
    <div className="stack" style={{ gap: 8 }}>
      {rows.map((w, i) => (
        <div key={i} className="permitrow">
          <span className="permitrow__days" role="group" aria-label={`Days for window ${i + 1}`}>
            {DAYS.map(([d, label]) => (
              <label key={d} className="check check--compact">
                <input type="checkbox" checked={w.days.includes(d)} onChange={(e) => put(i, { ...w, days: e.target.checked ? [...w.days, d].sort() : w.days.filter((x) => x !== d) })} /> {label}
              </label>
            ))}
          </span>
          <input className="input input--compact" type="time" aria-label="From" value={w.start} onChange={(e) => put(i, { ...w, start: e.target.value })} />
          <span>to</span>
          <input className="input input--compact" type="time" aria-label="To" value={w.end} onChange={(e) => put(i, { ...w, end: e.target.value })} />
          <button type="button" className="btn btn--outline btn--xs" onClick={() => onChange(rows.length > 1 ? rows.filter((_, j) => j !== i) : null)}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn btn--outline btn--xs" onClick={() => onChange([...rows, { days: [1, 2, 3, 4, 5], start: "20:00", end: "05:00" }])}>Add permit hours</button>
    </div>
  );
}

/** Drop windows with no days or no times before saving. */
export const cleanPermitHours = (v: PermitWindow[] | null | undefined): PermitWindow[] | null => {
  const ok = (v ?? []).filter((w) => w.days.length && /^\d{2}:\d{2}$/.test(w.start) && /^\d{2}:\d{2}$/.test(w.end));
  return ok.length ? ok : null;
};
